(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const $ = (selector, root = doc) => root.querySelector(selector);
  const $$ = (selector, root = doc) => Array.from(root.querySelectorAll(selector));

  const lockBody = (locked) => body.classList.toggle('sv-lock', Boolean(locked));

  /* Header */
  const header = $('[data-sv-header]');
  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  /* Mobile navigation */
  const mobileNav = $('[data-sv-mobile-nav]');
  const menuOpen = $('[data-sv-menu-open]');
  const menuClose = $('[data-sv-menu-close]');
  const setMenu = (open) => {
    if (!mobileNav) return;
    mobileNav.classList.toggle('is-open', open);
    mobileNav.setAttribute('aria-hidden', String(!open));
    if (menuOpen) menuOpen.setAttribute('aria-expanded', String(open));
    lockBody(open);
    if (open) window.setTimeout(() => menuClose?.focus(), 120);
  };
  menuOpen?.addEventListener('click', () => setMenu(true));
  menuClose?.addEventListener('click', () => setMenu(false));
  $$('a', mobileNav || doc).forEach((link) => link.addEventListener('click', () => setMenu(false)));

  /* Hero slideshow */
  $$('[data-sv-hero]').forEach((hero) => {
    const slides = $$('[data-sv-hero-slide]', hero);
    const dots = $$('[data-sv-hero-dot]', hero);
    const prev = $('[data-sv-hero-prev]', hero);
    const next = $('[data-sv-hero-next]', hero);
    if (slides.length < 2) return;
    let index = 0;
    let timer;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const show = (nextIndex, restart = true) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (restart && !reduced) start();
    };
    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(() => show(index + 1, false), 7000);
    };
    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
    hero.addEventListener('mouseenter', () => window.clearInterval(timer));
    hero.addEventListener('mouseleave', () => !reduced && start());
    hero.addEventListener('focusin', () => window.clearInterval(timer));
    hero.addEventListener('focusout', () => !reduced && start());
    if (!reduced) start();
  });

  /* Horizontal galleries */
  $$('[data-sv-gallery]').forEach((track) => {
    const section = track.closest('section') || track.parentElement;
    const prev = $('[data-sv-gallery-prev]', section);
    const next = $('[data-sv-gallery-next]', section);
    const amount = () => Math.max(280, track.clientWidth * 0.72);
    prev?.addEventListener('click', () => track.scrollBy({ left: -amount(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: amount(), behavior: 'smooth' }));
  });

  /* Scroll reveals */
  const revealItems = $$('[data-reveal]');
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  }

  /* Lightbox */
  const lightbox = $('[data-sv-lightbox-modal]');
  if (lightbox) {
    const image = $('[data-sv-lightbox-image]', lightbox);
    const caption = $('[data-sv-lightbox-caption]', lightbox);
    const close = $('[data-sv-lightbox-close]', lightbox);
    const previous = $('[data-sv-lightbox-prev]', lightbox);
    const following = $('[data-sv-lightbox-next]', lightbox);
    let items = [];
    let active = 0;
    let returnFocus = null;

    const renderLightbox = () => {
      const item = items[active];
      if (!item || !image) return;
      image.src = item.src;
      image.alt = item.alt || '';
      if (caption) caption.textContent = item.alt || '';
      const multiple = items.length > 1;
      if (previous) previous.hidden = !multiple;
      if (following) following.hidden = !multiple;
    };
    const closeLightbox = () => {
      lightbox.hidden = true;
      lockBody(false);
      returnFocus?.focus?.();
    };
    const openLightbox = (trigger) => {
      const group = trigger.closest('[data-sv-lightbox-group]');
      const triggers = group ? $$('[data-sv-lightbox]', group) : [trigger];
      items = triggers.map((button) => ({
        src: button.dataset.imageSrc || $('img', button)?.currentSrc || $('img', button)?.src || '',
        alt: button.dataset.imageAlt || $('img', button)?.alt || ''
      })).filter((item) => item.src);
      active = Math.max(0, triggers.indexOf(trigger));
      returnFocus = trigger;
      renderLightbox();
      lightbox.hidden = false;
      lockBody(true);
      window.setTimeout(() => close?.focus(), 40);
    };
    $$('[data-sv-lightbox]').forEach((trigger) => trigger.addEventListener('click', () => openLightbox(trigger)));
    close?.addEventListener('click', closeLightbox);
    previous?.addEventListener('click', () => { active = (active - 1 + items.length) % items.length; renderLightbox(); });
    following?.addEventListener('click', () => { active = (active + 1) % items.length; renderLightbox(); });
    lightbox.addEventListener('click', (event) => { if (event.target === lightbox) closeLightbox(); });
    doc.addEventListener('keydown', (event) => {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft' && items.length > 1) { active = (active - 1 + items.length) % items.length; renderLightbox(); }
      if (event.key === 'ArrowRight' && items.length > 1) { active = (active + 1) % items.length; renderLightbox(); }
    });
  }

  /* Sivana two-step enquiry and viewing funnel */
  const overlay = $('[data-sv-funnel-overlay]');
  const form = $('#SivanaEnquiryForm');
  if (overlay && form) {
    const steps = $$('[data-sv-step]', form);
    const flow = $('[data-sv-funnel-flow]', form);
    const success = $('[data-sv-funnel-success]', form);
    const currentEl = $('[data-sv-step-current]', form);
    const labelEl = $('[data-sv-progress-label]', form);
    const bar = $('[data-sv-progress-bar]', form);
    const next = $('[data-sv-funnel-next]', form);
    const back = $('[data-sv-funnel-back]', form);
    const submit = $('[data-sv-funnel-submit]', form);
    const submitLabel = $('[data-sv-submit-label]', form);
    const validation = $('[data-sv-funnel-validation]', form);
    const bookingFields = $('[data-sv-booking-fields]', form);
    const brochureContext = $('[data-sv-brochure-context]', form);
    const resaleContext = $('[data-sv-resale-context]', form);
    const detailsKicker = $('[data-sv-details-kicker]', form);
    const detailsTitle = $('[data-sv-details-title]', form);
    const detailsIntro = $('[data-sv-details-intro]', form);
    const messageOptional = $('[data-sv-message-optional]', form);
    const messageField = $('[data-sv-message-field]', form);
    const dateInput = $('[data-sv-viewing-date]', form);
    const timeInput = $('#SivanaViewingTime', form);
    const emailInput = $('#SivanaEmail', form);
    const messageInput = $('#SivanaMessage', form);
    const configNode = $('[data-sv-funnel-config]');
    let config = {};
    let activeStep = 1;
    let returnFocus = null;
    let activeIntent = 'brochure';
    let activeListing = '';
    let minimumDate = '';

    try { config = configNode ? JSON.parse(configNode.textContent || '{}') : {}; } catch (_) { config = {}; }

    const radioValue = (name) => form.querySelector(`input[name="${name}"]:checked`)?.value || '';
    const selectedGoal = () => form.querySelector('input[name="sv_goal"]:checked');
    const goalKey = () => selectedGoal()?.dataset.svGoal || '';
    const fieldValue = (selector) => $(selector, form)?.value?.trim() || '';
    const setHidden = (selector, value) => { const input = $(selector, form); if (input) input.value = value || ''; };

    const setMinimumViewingDate = () => {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Bangkok',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date()).reduce((result, part) => {
        if (part.type !== 'literal') result[part.type] = Number(part.value);
        return result;
      }, {});
      const minimum = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1, 12));
      minimumDate = minimum.toISOString().slice(0, 10);
      if (dateInput) dateInput.min = minimumDate;
    };

    const viewingSummary = () => {
      if (goalKey() !== 'viewing') return '';
      return [
        fieldValue('#SivanaViewingMode'),
        fieldValue('#SivanaViewingDate'),
        fieldValue('#SivanaViewingTime')
      ].filter(Boolean).join(' · ');
    };

    const data = () => ({
      goal: radioValue('sv_goal'),
      goalKey: goalKey(),
      viewing: viewingSummary(),
      viewingMode: fieldValue('#SivanaViewingMode'),
      viewingDate: fieldValue('#SivanaViewingDate'),
      viewingTime: fieldValue('#SivanaViewingTime'),
      name: fieldValue('#SivanaName'),
      email: fieldValue('#SivanaEmail'),
      phone: fieldValue('#SivanaPhone'),
      message: fieldValue('#SivanaMessage'),
      listing: activeListing,
      intent: activeIntent,
      page: window.location.href
    });

    const syncForm = () => {
      const d = data();
      const isResale = d.goalKey === 'resale';
      const development = isResale ? 'Selected Sivana owner resale' : 'Sivana Hills';
      const subjects = {
        viewing: 'Sivana Hills viewing request',
        brochure: 'Sivana Hills brochure request',
        resale: 'Sivana resale enquiry',
        question: 'Sivana Villas website question'
      };
      const subject = subjects[d.goalKey] || 'Sivana Villas website enquiry';
      setHidden('[data-sv-hidden-subject]', subject);
      setHidden('[data-sv-hidden-development]', development);
      setHidden('[data-sv-hidden-goal]', d.goal);
      setHidden('[data-sv-hidden-viewing]', d.viewing);
      setHidden('[data-sv-hidden-listing]', d.listing || window.location.pathname);
      const bodyText = [
        `Purpose: ${d.goal || 'Not provided'}`,
        `Development: ${development}`,
        `Requested appointment: ${d.viewing || 'Not applicable'}`,
        `Page or listing: ${d.listing || window.location.href}`,
        `Name: ${d.name || 'Not provided'}`,
        `WhatsApp / phone: ${d.phone || 'Not provided'}`,
        `Email: ${d.email || 'Not provided'}`,
        `Message: ${d.message || 'None'}`
      ].join('\n');
      setHidden('[data-sv-hidden-body]', bodyText);
      return { ...d, development, subject, body: bodyText };
    };

    const intentContent = {
      viewing: {
        kicker: 'Private viewing',
        title: 'Choose a date and time.',
        intro: 'Select an in-person or live video date and preferred time from tomorrow onwards. Our team will confirm the requested time with you directly.',
        submit: 'Request Viewing'
      },
      brochure: {
        kicker: 'Sivana Hills brochure',
        title: 'Where should we send it?',
        intro: 'Add your name and email address and we will send the Sivana Hills brochure and floor plans.',
        submit: 'Request Brochure'
      },
      resale: {
        kicker: 'Selected owner resales',
        title: 'Ask about a resale.',
        intro: 'Leave your details and any preference you have. The team can confirm whether an individual owner currently has a villa listed.',
        submit: 'Send Resale Enquiry'
      },
      question: {
        kicker: 'Ask us a question',
        title: 'What would you like to know?',
        intro: 'Add your contact details and write your question below.',
        submit: 'Send Question'
      }
    };

    const updateIntentUI = () => {
      const key = goalKey() || 'brochure';
      const content = intentContent[key] || intentContent.brochure;
      if (bookingFields) bookingFields.hidden = key !== 'viewing';
      if (brochureContext) brochureContext.hidden = key !== 'brochure';
      if (resaleContext) resaleContext.hidden = key !== 'resale';
      if (detailsKicker) detailsKicker.textContent = content.kicker;
      if (detailsTitle) detailsTitle.textContent = content.title;
      if (detailsIntro) detailsIntro.textContent = content.intro;
      if (submitLabel) submitLabel.textContent = content.submit;
      if (dateInput) dateInput.required = key === 'viewing';
      if (timeInput) timeInput.required = key === 'viewing';
      if (emailInput) emailInput.required = true;
      if (messageField) messageField.hidden = !['resale', 'question'].includes(key);
      if (messageInput) messageInput.required = key === 'question';
      if (messageOptional) messageOptional.textContent = key === 'question' ? '(required)' : '(optional)';
    };

    const setStep = (stepNumber, focus = true) => {
      activeStep = Math.min(steps.length, Math.max(1, stepNumber));
      steps.forEach((step) => {
        const active = Number(step.dataset.svStep) === activeStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
      if (currentEl) currentEl.textContent = String(activeStep);
      if (bar) bar.style.width = `${(activeStep / steps.length) * 100}%`;
      const activeNode = steps.find((step) => Number(step.dataset.svStep) === activeStep);
      if (labelEl) labelEl.textContent = activeNode?.dataset.svStepLabel || '';
      if (back) back.hidden = activeStep === 1;
      if (next) next.hidden = activeStep === steps.length;
      if (submit) submit.hidden = activeStep !== steps.length;
      if (validation) validation.textContent = '';
      if (activeStep === 2) updateIntentUI();
      overlay.querySelector('.sv-funnel__main')?.scrollTo({ top: 0, behavior: 'smooth' });
      if (focus) {
        window.setTimeout(() => {
          let focusable = activeNode?.querySelector('input:not([type="hidden"]), select, textarea, button');
          if (activeStep === 2) {
            focusable = goalKey() === 'viewing' ? $('#SivanaViewingMode', form) : $('#SivanaName', form);
          }
          focusable?.focus({ preventScroll: true });
        }, 70);
      }
    };

    const validateStep = (stepNumber) => {
      if (validation) validation.textContent = '';
      if (stepNumber === 1 && !goalKey()) {
        if (validation) validation.textContent = 'Please choose one option before continuing.';
        form.querySelector('input[name="sv_goal"]')?.focus();
        return false;
      }
      if (stepNumber === 2) {
        if (goalKey() === 'viewing') {
          const date = fieldValue('#SivanaViewingDate');
          const time = fieldValue('#SivanaViewingTime');
          if (!date) {
            if (validation) validation.textContent = 'Please choose your preferred viewing date.';
            dateInput?.focus();
            return false;
          }
          if (minimumDate && date < minimumDate) {
            if (validation) validation.textContent = 'Please choose a viewing date from tomorrow onwards.';
            dateInput?.focus();
            return false;
          }
          if (!time) {
            if (validation) validation.textContent = 'Please choose a preferred time.';
            timeInput?.focus();
            return false;
          }
        }
        for (const selector of ['#SivanaName', '#SivanaEmail']) {
          const input = $(selector, form);
          if (!input?.checkValidity()) {
            if (validation) validation.textContent = input?.validationMessage || 'Please complete your contact details.';
            input?.focus();
            return false;
          }
        }
        if (goalKey() === 'question' && !fieldValue('#SivanaMessage')) {
          if (validation) validation.textContent = 'Please add your question before sending.';
          messageInput?.focus();
          return false;
        }
        const consent = $('input[name="sv_consent"]', form);
        if (!consent?.checked) {
          if (validation) validation.textContent = 'Please confirm consent so the team can respond to your enquiry.';
          consent?.focus();
          return false;
        }
      }
      return true;
    };

    const selectGoal = (key) => {
      const input = form.querySelector(`input[name="sv_goal"][data-sv-goal="${key}"]`);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const applyIntent = (intent) => {
      activeIntent = intent || '';
      let key = '';
      if (/^(viewing|video-viewing)$/.test(activeIntent)) key = 'viewing';
      else if (/^(resale|compare-new-resale)$/.test(activeIntent)) key = 'resale';
      else if (activeIntent === 'question') key = 'question';
      else if (activeIntent) key = 'brochure';

      const typeMatch = activeIntent.match(/^type-(a|as|b|c)$/i);
      if (typeMatch && !activeListing) activeListing = `Sivana Hills Type ${typeMatch[1].toUpperCase()}`;
      if (key) selectGoal(key);
      if (activeIntent === 'video-viewing') {
        const mode = $('#SivanaViewingMode', form);
        if (mode) mode.value = 'Live video viewing';
      }
      updateIntentUI();
      return key;
    };

    const resetFunnel = () => {
      form.reset();
      if (flow) flow.hidden = false;
      if (success) success.hidden = true;
      if (submit) submit.disabled = false;
      setMinimumViewingDate();
      updateIntentUI();
      setStep(1, false);
    };

    const openFunnel = (trigger) => {
      returnFocus = trigger || doc.activeElement;
      activeListing = trigger?.dataset.svListing || trigger?.closest('[data-sv-listing-title]')?.dataset.svListingTitle || '';
      resetFunnel();
      const key = applyIntent(trigger?.dataset.svIntent || '');
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      setMenu(false);
      lockBody(true);
      setStep(key ? 2 : 1);
    };

    const closeFunnel = () => {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      lockBody(false);
      returnFocus?.focus?.();
    };

    $$('input[name="sv_goal"]', form).forEach((input) => input.addEventListener('change', updateIntentUI));
    $$('[data-sv-funnel]').forEach((trigger) => trigger.addEventListener('click', () => openFunnel(trigger)));
    $$('[data-sv-funnel-close]', overlay).forEach((button) => button.addEventListener('click', closeFunnel));
    overlay.addEventListener('click', (event) => { if (event.target === overlay) closeFunnel(); });
    next?.addEventListener('click', () => { if (validateStep(activeStep)) setStep(activeStep + 1); });
    back?.addEventListener('click', () => setStep(activeStep - 1));

    form.addEventListener('submit', (event) => {
      if (!validateStep(2)) {
        event.preventDefault();
        return;
      }
      const payload = syncForm();
      if (config.webhook) {
        try {
          fetch(config.webhook, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ source: 'Sivana Villas Shopify', ...payload }),
            keepalive: true,
            mode: 'no-cors'
          }).catch(() => {});
        } catch (_) { /* Shopify contact form remains the source of truth. */ }
      }
      if (submit) {
        submit.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Sending…';
      }
    });

    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) closeFunnel();
    });

    setMinimumViewingDate();

    if (success && !success.hidden) {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      if (flow) flow.hidden = true;
      lockBody(true);
    }
  }

  /* Keep floating contact clear of the on-screen keyboard. */
  const contactDock = $('[data-sv-contact-dock]');
  if (contactDock) {
    const formControls = $$('input, select, textarea');
    formControls.forEach((control) => {
      control.addEventListener('focus', () => contactDock.classList.add('is-input-active'));
      control.addEventListener('blur', () => contactDock.classList.remove('is-input-active'));
    });
  }

  /* Keep Shopify theme-editor changes interactive without a full refresh. */
  doc.addEventListener('shopify:section:load', () => window.location.reload());
})();
